import { Suspense } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';
import { Helmet } from 'react-helmet-async';
import {
  OrganizationSchema,
  WebSiteSchema,
  LocalBusinessSchema,
  CookieBanner,
  GoogleAdsConsentBridge,
} from '@afrilink/ui';
// CookieBanner RE-ENABLED 2026-07-08: the Google Ads conversion tag sets an
// advertising cookie, so we now have a non-essential category that requires
// POPIA consent. Consent Mode v2 defaults to denied; "Accept all" promotes it
// via GoogleAdsConsentBridge. See CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md
// (supersedes the "no trackers, no banner" stance in
// memory/marketing_v2_architecture_decisions.md Q3).
import { siteConfig, capturePartnerTokenFromSearch } from '@afrilink/config';
import { Header } from './Header';
import { Footer } from './Footer';
import { DomainSearch } from '../components/DomainSearch';

/**
 * Pages that mount the full hero DomainSearch themselves — the site-wide
 * strip stays off these so search appears exactly once per page.
 */
const HERO_SEARCH_PATHS = new Set(['/', '/domains']);

export function RootLayout() {
  const { pathname, search } = useLocation();
  const showSearchStrip = !HERO_SEARCH_PATHS.has(pathname.replace(/\/$/, '') || '/');

  // Partner attribution (2026-09-25). `/p/<token>` forwards to
  // `/hosting?pt=<token>`; a visitor who then opens that URL in a DIFFERENT
  // browser (WhatsApp's "Open in Chrome", a copied link) arrives here with an
  // empty sessionStorage, and this is what re-captures the token so their
  // journey is attributed exactly like the first. Captured in the render body
  // so every child CTA rendered below — all of which go through getPortalUrl —
  // already sees it. Idempotent and shape-checked; a URL with no `pt` is a no-op.
  capturePartnerTokenFromSearch(search);

  return (
    <>
      <Helmet
        defaultTitle={`${siteConfig.name} | Web Hosting South Africa`}
        titleTemplate={`%s | ${siteConfig.name}`}
      >
        <html lang="en" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content={siteConfig.brand.themeColor} />
        <meta name="author" content={siteConfig.name} />
        <meta name="publisher" content={siteConfig.legalName} />
        <meta name="keywords" content={siteConfig.keywords.join(', ')} />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1"
        />
      </Helmet>

      <OrganizationSchema />
      <WebSiteSchema />
      <LocalBusinessSchema />

      <div className="bg-neutral-950 text-white">
        <Header />
        <main id="main-content">
          {/* Domain search at the front of every page (2026-07-02). Home +
              Domains mount the hero variant inside their own hero instead.
              pt-20 clears the fixed header, which is transparent at the top
              of the page and reads cleanly over this dark band. */}
          {showSearchStrip && <DomainSearch variant="strip" className="pt-20" />}
          <Suspense fallback={<div className="p-8">Loading…</div>}>
            <Outlet />
          </Suspense>
        </main>
        <Footer />
      </div>
      <CookieBanner />
      <GoogleAdsConsentBridge />
      <ScrollRestoration />
    </>
  );
}
